import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { ExternalLink, ImagePlus, Loader2, MessageCircle } from 'lucide-react';
import type { ContactConfig } from '@shared/api.interface';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

type Spot = 'dialog' | 'detail' | 'passcode' | 'header';
const SPOTS: Array<[Spot, string, string]> = [
  ['dialog', '客服弹窗', '家长点任意一处客服按钮后弹出'],
  ['detail', '主题详情', '主题详情底部，虚线框是你正在改的文字'],
  ['passcode', '兑换码弹窗', '家长点“输入兑换码”后看到的弹窗'],
  ['header', '首页顶部', '首页最上方，“登录”按钮左边'],
];
const cardClass =
  'space-y-4 rounded-2xl border border-border bg-card p-5 sm:px-6';
// 预览里标出“正在改的文字”
const markClass =
  'rounded-lg outline-2 outline-offset-[3px] outline-dashed outline-primary';

function Section({
  index,
  title,
  description,
  className = '',
  children,
}: {
  index: number;
  title: string;
  description: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cardClass + ' ' + className}>
      <div className="flex items-baseline gap-2.5">
        <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-orange-50 text-xs font-bold text-orange-800">
          {index}
        </span>
        <div>
          <h3 className="text-[17px] font-semibold text-foreground">{title}</h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  note,
  children,
  divided,
}: {
  label: string;
  note?: string;
  children: ReactNode;
  divided?: boolean;
}) {
  return (
    <label
      className={
        'flex flex-col gap-2 text-sm font-medium text-foreground' +
        (divided ? ' border-t border-border/60 pt-4' : '')
      }
    >
      <span>
        {label}
        {note && (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {note}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

export function ContactConfigPanel({
  config,
  savedConfig,
  onChange,
  onSave,
  isSaving,
  isUploading,
  onUploadQr,
}: {
  config: ContactConfig;
  savedConfig: ContactConfig;
  onChange: (patch: Partial<ContactConfig>) => void;
  onSave: () => void;
  isSaving: boolean;
  isUploading: boolean;
  onUploadQr: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const [spot, setSpot] = useState<Spot>('dialog');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const isQr = config.method !== 'link';
  const dirty = JSON.stringify(config) !== JSON.stringify(savedConfig);
  const text =
    (key: keyof ContactConfig) => (e: ChangeEvent<HTMLInputElement>) =>
      onChange({ [key]: e.target.value } as Partial<ContactConfig>);
  const focus = (next: Spot) => () => setSpot(next);

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="min-w-0 space-y-4">
        <section
          className={cardClass + (config.enabled ? '' : ' border-amber-200')}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-[17px] font-semibold text-foreground">
                客服入口
              </h3>
              <p className="mt-1 text-[13px] text-muted-foreground">
                总开关。关掉后，家长在任何地方都看不到客服按钮。
              </p>
            </div>
            <Switch
              aria-label="客服入口总开关"
              checked={config.enabled}
              onCheckedChange={(enabled) => onChange({ enabled })}
            />
          </div>
          {!config.enabled && (
            <p className="rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
              已关闭：家长现在看不到任何客服入口。下面的设置会保留，重新打开后生效。
            </p>
          )}
        </section>

        <div
          className={
            'space-y-4 transition-opacity duration-150 ease-out' +
            (config.enabled ? '' : ' opacity-55')
          }
        >
          <Section
            index={1}
            title="联系方式"
            description={`家长点“${config.button_text || '联系客服'}”之后看到什么`}
          >
            <div
              role="group"
              aria-label="承接方式"
              className="inline-flex gap-0.5 rounded-xl bg-muted p-1"
            >
              {(
                [
                  ['qr', '二维码弹窗'],
                  ['link', '外部链接'],
                ] as const
              ).map(([value, label]) => {
                const on = config.method === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      onChange({ method: value });
                      setSpot('dialog');
                    }}
                    className={
                      'min-h-[38px] whitespace-nowrap rounded-[9px] px-4 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-cta ' +
                      (on
                        ? 'bg-card font-semibold text-cta shadow-sm'
                        : 'text-accent-foreground hover:text-foreground')
                    }
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {isQr ? (
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="微信号">
                  <Input
                    className="h-11"
                    value={config.wechat_id}
                    onChange={text('wechat_id')}
                    onFocus={focus('dialog')}
                    placeholder="例如：abc123"
                  />
                </Field>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground">
                    二维码图片
                  </span>
                  <div className="flex items-center gap-3.5 rounded-xl border border-dashed border-orange-200 bg-orange-50/40 px-3 py-2.5">
                    {config.qr_image_url?.trim() ? (
                      <img
                        src={config.qr_image_url.trim()}
                        alt="当前客服二维码"
                        className="h-16 w-16 shrink-0 rounded-lg border border-border bg-white object-contain p-1"
                      />
                    ) : (
                      <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground">
                        <ImagePlus aria-hidden className="h-5 w-5" />
                      </span>
                    )}
                    <div className="flex min-w-0 flex-col items-start gap-1.5">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={onUploadQr}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="h-9 rounded-[10px]"
                        disabled={isUploading}
                        onFocus={focus('dialog')}
                        onClick={() => fileRef.current?.click()}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            上传中…
                          </>
                        ) : config.qr_image_url?.trim() ? (
                          '更换图片'
                        ) : (
                          '上传图片'
                        )}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        正方形最佳，支持 PNG / JPG / WebP
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Field label="客服链接地址">
                <Input
                  className="h-11"
                  value={config.link_url}
                  onChange={text('link_url')}
                  onFocus={focus('dialog')}
                  placeholder="https://..."
                />
                <span className="text-xs font-normal text-muted-foreground">
                  家长点按钮后，直接在新页面打开这个链接（例如企业微信客服链接）
                </span>
              </Field>
            )}
          </Section>

          <Section
            index={2}
            title="客服弹窗"
            description="显示在二维码和微信号上方"
            className={isQr ? '' : 'bg-background/60'}
          >
            {!isQr && (
              <p className="rounded-xl bg-muted px-3 py-2.5 text-[13px] text-accent-foreground">
                当前是“外部链接”方式，不会弹出客服弹窗，这两项暂时不生效。
              </p>
            )}
            <Field label="弹窗标题">
              <Input
                className="h-11"
                value={config.title}
                onChange={text('title')}
                onFocus={focus('dialog')}
                placeholder="购买 / 续费 / 单独获取"
              />
            </Field>
            <Field label="弹窗说明">
              <Input
                className="h-11"
                value={config.description}
                onChange={text('description')}
                onFocus={focus('dialog')}
                placeholder="支持会员开通、续费、单独获取主题、找回兑换码"
              />
            </Field>
          </Section>

          <Section
            index={3}
            title="各处的客服按钮"
            description="家长会在这三个地方看到客服按钮"
          >
            <Field label="按钮文字" note="三处通用">
              <Input
                className="h-11"
                value={config.button_text}
                onChange={text('button_text')}
                onFocus={focus('detail')}
                placeholder="联系客服"
              />
            </Field>
            <div className="flex items-center justify-between gap-4 border-t border-border/60 pt-4">
              <div>
                <p className="text-sm font-medium text-foreground">
                  首页顶部 · “登录”旁边
                </p>
                <p className="mt-1 text-[13px] text-muted-foreground">
                  只显示按钮，不带提示文字
                </p>
              </div>
              <Switch
                aria-label="首页顶部显示客服按钮"
                checked={config.show_header_entry}
                onFocus={focus('header')}
                onCheckedChange={(show_header_entry) => {
                  onChange({ show_header_entry });
                  setSpot('header');
                }}
              />
            </div>
            <Field label="主题详情 · 按钮旁的提示" divided>
              <Input
                className="h-11"
                value={config.detail_description}
                onChange={text('detail_description')}
                onFocus={focus('detail')}
                placeholder="支持兑换码领取、单独获取、续费咨询"
              />
            </Field>
            <Field label="兑换码弹窗 · 按钮旁的提示" divided>
              <Input
                className="h-11"
                value={config.passcode_hint}
                onChange={text('passcode_hint')}
                onFocus={focus('passcode')}
                placeholder="兑换码通常为订单号，需获取可联系客服"
              />
            </Field>
          </Section>
        </div>

        <div className="flex items-center justify-end gap-3.5">
          <span
            className="text-[13px] text-muted-foreground"
            aria-live="polite"
          >
            {dirty ? '有未保存的修改' : '所有修改已保存'}
          </span>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="h-11 rounded-xl bg-cta px-6 font-semibold text-cta-foreground hover:bg-cta-hover"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                保存中…
              </>
            ) : (
              '保存配置'
            )}
          </Button>
        </div>
      </div>

      <ContactPreview config={config} spot={spot} onSpot={setSpot} />
    </div>
  );
}

// 客服配置的实时预览：按家长端四处入口的样子显示当前填写的内容（未保存也会更新）
function ContactPreview({
  config,
  spot,
  onSpot,
}: {
  config: ContactConfig;
  spot: Spot;
  onSpot: (spot: Spot) => void;
}) {
  const on = config.enabled;
  const button = config.button_text || '联系客服';
  const wechatId = config.wechat_id?.trim();
  const qr = config.qr_image_url?.trim();
  return (
    <aside
      aria-label="家长看到的效果"
      className="min-w-0 space-y-3 xl:sticky xl:top-6"
    >
      <p className="text-sm font-semibold text-accent-foreground">
        家长看到的效果
        <span className="font-normal text-muted-foreground">
          {' '}
          · 未保存也实时更新
        </span>
      </p>
      <div
        role="group"
        aria-label="预览位置"
        className="grid grid-cols-4 gap-1 rounded-xl bg-muted p-1"
      >
        {SPOTS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={spot === value}
            onClick={() => onSpot(value)}
            className={
              'min-h-9 whitespace-nowrap rounded-[9px] px-1 text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-cta ' +
              (spot === value
                ? 'bg-card font-semibold text-cta shadow-sm'
                : 'text-accent-foreground hover:text-foreground')
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex min-h-[380px] flex-col justify-center rounded-2xl border border-border bg-[#f3efe9] px-4 py-5">
        {spot === 'dialog' &&
          (config.method === 'link' ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-5 text-center">
              <ExternalLink aria-hidden className="h-7 w-7 text-cta" />
              <p className="text-sm font-semibold text-foreground">
                不弹窗，直接打开链接
              </p>
              <p className="break-all text-xs text-muted-foreground">
                {config.link_url || '（还没填写链接地址）'}
              </p>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[290px] space-y-3 rounded-2xl bg-white p-5 shadow-[0_10px_30px_rgba(60,40,20,0.14)]">
              <div className="text-center">
                <p className="text-base font-bold text-foreground">
                  {config.title || '联系客服'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {config.description ||
                    '支持购买、续费、单独获取主题与找回兑换码'}
                </p>
              </div>
              {wechatId && (
                <div className="flex items-center justify-between gap-2 rounded-xl border bg-muted/20 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground">微信号</p>
                    <p className="truncate font-mono text-sm text-foreground">
                      {wechatId}
                    </p>
                  </div>
                  <span className="rounded-md border bg-white px-2 py-0.5 text-xs">
                    复制
                  </span>
                </div>
              )}
              {qr ? (
                <div className="flex flex-col items-center rounded-xl border px-3 py-3">
                  <img
                    src={qr}
                    alt="客服二维码预览"
                    className="h-32 w-32 rounded object-contain"
                  />
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    可截图扫码添加微信
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed bg-muted/20 p-3 text-center text-xs text-muted-foreground">
                  暂未配置二维码图片
                </div>
              )}
            </div>
          ))}

        {spot === 'detail' && (
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(60,40,20,0.12)]">
            <div className="flex h-28 items-end bg-[#e8dccb] px-3.5 py-3">
              <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-xs text-orange-800">
                传统文化
              </span>
            </div>
            <div className="px-3.5 pt-3">
              <p className="text-[17px] font-bold text-foreground">花木兰</p>
              <p className="mt-1 text-xs text-muted-foreground">
                低年级 · 约 3 分钟
              </p>
            </div>
            <div className="mt-3 space-y-2.5 border-t border-border/60 px-3.5 py-3">
              {on && (
                <p
                  className={
                    'px-1 text-[13px] text-muted-foreground ' + markClass
                  }
                >
                  {config.detail_description}
                </p>
              )}
              <div className="flex gap-2">
                {on && (
                  <span
                    className={
                      'flex h-11 items-center whitespace-nowrap rounded-xl border border-orange-200 px-4 text-sm font-semibold text-orange-800 ' +
                      markClass
                    }
                  >
                    {button}
                  </span>
                )}
                <span className="flex h-11 flex-1 items-center justify-center rounded-xl bg-primary text-sm font-semibold text-primary-foreground">
                  输入兑换码领取
                </span>
              </div>
            </div>
          </div>
        )}

        {spot === 'passcode' && (
          <div className="space-y-3 rounded-2xl bg-white px-4 py-5 shadow-[0_10px_30px_rgba(60,40,20,0.12)]">
            <p className="text-lg font-bold text-foreground">输入兑换码</p>
            <div className="flex h-11 items-center rounded-[10px] border px-3 text-sm text-muted-foreground">
              请输入兑换码
            </div>
            <span className="flex h-11 items-center justify-center rounded-xl bg-primary text-sm font-semibold text-primary-foreground">
              验证并领取
            </span>
            {on && (
              <div
                className={
                  'flex items-center gap-2 rounded-xl border border-dashed border-orange-200 px-3 py-1.5 ' +
                  markClass
                }
              >
                <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                  {config.passcode_hint}
                </span>
                <span className="shrink-0 px-1 py-1.5 text-[13px] font-semibold text-orange-800">
                  {button}
                </span>
              </div>
            )}
          </div>
        )}

        {spot === 'header' && (
          <div>
            <div className="flex items-center justify-between gap-2 rounded-[14px] bg-white px-3 py-2.5 shadow-[0_6px_20px_rgba(60,40,20,0.10)]">
              <span className="flex min-w-0 items-center gap-2">
                <span className="h-7 w-7 shrink-0 rounded-lg bg-primary" />
                <span className="whitespace-nowrap text-sm font-bold text-foreground">
                  少儿英语说
                </span>
              </span>
              <span className="flex items-center gap-1">
                {on && config.show_header_entry && (
                  <span
                    className={
                      'flex h-[34px] items-center gap-1 whitespace-nowrap px-2 text-[13px] text-muted-foreground ' +
                      markClass
                    }
                  >
                    <MessageCircle aria-hidden className="h-4 w-4" />
                    {button}
                  </span>
                )}
                <span className="flex h-[34px] items-center rounded-full border border-orange-200 px-3.5 text-[13px] font-semibold text-orange-800">
                  登录
                </span>
              </span>
            </div>
            {on && !config.show_header_entry && (
              <p className="mt-3 text-center text-xs text-muted-foreground">
                首页顶部不显示客服按钮
              </p>
            )}
          </div>
        )}

        {!on && (
          <p className="mt-3.5 rounded-xl bg-amber-50 p-2 text-center text-xs text-amber-900">
            客服入口已关闭，这里不会出现客服按钮
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {spot === 'dialog' && config.method === 'link'
          ? '外部链接方式：家长点按钮直接打开链接'
          : SPOTS.find(([value]) => value === spot)![2]}
      </p>
    </aside>
  );
}
